import { Suspense, useEffect, useState } from 'react';

import { analytics } from '@/features/analytics/service';
import { AceBadge } from '@/features/subscription/components/aceBadge';
import { PlanChoicebox } from '@/features/subscription/components/planChoicebox';
import { PlanChoiceboxSkeleton } from '@/features/subscription/components/planChoicebox/planChoiceboxSkeleton';
import {
  SubscriptionDialogBody,
  SubscriptionDialogContent,
  SubscriptionDialogDescription,
  SubscriptionDialogFooter,
  SubscriptionDialogHeader,
  SubscriptionDialogHeading,
  SubscriptionDialogNotice,
  SubscriptionDialogSection,
  SubscriptionDialogTitle,
} from '@/features/subscription/components/subscriptionDialog';
import type { Package } from '@/integrations/revenuecat/types';
import { Button } from '@/shared/components/ui/button';

import {
  usePaywallCheckout,
  type UsePaywallCheckoutOptions,
} from '../hooks/usePaywallCheckout';

export interface PickPlanPageProps extends UsePaywallCheckoutOptions {}

export function PickPlanPage({
  onCheckoutOpen,
  onCheckoutClose,
}: PickPlanPageProps) {
  const [selectedPackage, setSelectedPackage] = useState<Package>();
  const { handlePurchase, isPending } = usePaywallCheckout({
    onCheckoutOpen,
    onCheckoutClose,
  });

  useEffect(() => {
    analytics.captureEvent('Duos Subscription Plans Viewed', {});
  }, []);

  const handleSelectPackage = (pkg: Package) => {
    setSelectedPackage(pkg);
    analytics.captureEvent('Duos Subscription Plan Selected', {
      plan_id: pkg.identifier,
      plan_type: pkg.packageType,
      price: pkg.product.priceString,
    });
  };

  const handlePurchaseClick = () => {
    if (!selectedPackage) return;
    handlePurchase(selectedPackage);
  };

  return (
    <SubscriptionDialogContent>
      <SubscriptionDialogBody>
        <SubscriptionDialogHeader>
          <AceBadge />
          <SubscriptionDialogHeading>
            <SubscriptionDialogTitle>
              Become an <span className="text-cardpink-400">Ace!</span>
            </SubscriptionDialogTitle>
            <SubscriptionDialogDescription>
              Ace includes: See who liked you, Unlimited swipes, 10 daily
              reveals &amp; Discovery priority
            </SubscriptionDialogDescription>
          </SubscriptionDialogHeading>
        </SubscriptionDialogHeader>

        <SubscriptionDialogSection>
          <Suspense fallback={<PlanChoiceboxSkeleton />}>
            <PlanChoicebox
              selectedPackageId={
                selectedPackage ? selectedPackage.identifier : undefined
              }
              onSelectPackage={handleSelectPackage}
            />
          </Suspense>
        </SubscriptionDialogSection>

        <SubscriptionDialogNotice>
          Cancel anytime, no penalties or fees
        </SubscriptionDialogNotice>
      </SubscriptionDialogBody>

      <SubscriptionDialogFooter className="flex-col gap-5">
        <Button
          variant="destructive"
          size="lg"
          className="w-full"
          onClick={handlePurchaseClick}
          loading={isPending}
          disabled={isPending || !selectedPackage}
        >
          Continue
          {selectedPackage && ` - ${selectedPackage.product.priceString} total`}
        </Button>

        <p className="font-nunito text-cold-400 text-center text-xs leading-4">
          By continuing, you agree to our{' '}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="font-bold underline"
          >
            Terms
          </a>{' '}
          and{' '}
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="font-bold underline"
          >
            Privacy Policy
          </a>
          . Subscription renews automatically until you cancel in your account
          settings.
        </p>
      </SubscriptionDialogFooter>
    </SubscriptionDialogContent>
  );
}
